import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import { subscriptionAPI, profileAPI } from '../../utils/api';
import { PricingPlan } from '../../types/subscription';
import Layout from '../../components/layout/Layout';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Warning, X, ArrowCounterClockwise } from '@phosphor-icons/react';
import { trackCheckoutStarted, trackPlanChanged } from '../../utils/analytics';

const PLANS: PricingPlan[] = [
  {
    id: 'specialist',
    name: 'The Specialist',
    monthlyPrice: 19.99,
    annualPrice: 197,
    courseLimit: 1,
    features: [
      'Unlimited tutoring for 1 active class',
      'Step-by-step explanations',
      'Curriculum alignment',
      'Adaptive practice and review',
      'Monthly progress summary',
    ],
    goodToKnow: 'Focuses on one subject at a time. Study as often as you like within that subject.',
  },
  {
    id: 'academic',
    name: 'The Academic',
    monthlyPrice: 29.99,
    annualPrice: 297,
    courseLimit: 3,
    features: [
      'Unlimited tutoring for 2–3 active classes',
      'Everything in The Specialist',
      'Practice across subjects',
      'Weekly parent summaries',
      'Skill gap identification',
      'AI-generated practice sets',
    ],
    goodToKnow: 'Designed for managing multiple subjects with regular parent insights.',
  },
  {
    id: 'scholar',
    name: 'The Scholar',
    monthlyPrice: 49.99,
    annualPrice: 497,
    courseLimit: '4+',
    features: [
      'Unlimited tutoring for 4+ active classes',
      'Everything in The Academic',
      'Exam and test-prep support',
      'Advanced progress tracking',
      'Custom study guides / personalized study plans',
      'Early struggle alerts',
      'Real-time parent dashboard',
      'Priority support',
    ],
    goodToKnow: 'Full academic oversight and proactive support across all subjects.',
  },
];

// Tier order for upgrade/downgrade comparison
const TIER_ORDER: Record<string, number> = { specialist: 0, academic: 1, scholar: 2 };
// Numeric course limits (scholar = unlimited represented as 999)
const PLAN_COURSE_LIMITS: Record<string, number> = { specialist: 1, academic: 3, scholar: 999 };

type PlanAction = 'current' | 'upgrade' | 'downgrade' | 'subscribe';

interface DowngradeModalState {
  targetPlan: PricingPlan;
  selectedIds: string[];
  isSubscribe?: boolean;
}

interface UnarchiveModalState {
  planId: string;
  availableSlots: number;
  archivedSubjects: Array<{ id: string; name: string }>;
  selectedIds: string[];
}

export default function SubscriptionSettings() {
  const { userProfile, refreshProfile } = useAuth();
  const { subscription, isActive, isTrial, isComplimentary, daysLeft, tier } = useSubscription();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Read promo code from URL once, store in state, then immediately strip it from the URL
  const [promoCode] = useState<string | undefined>(() => searchParams.get('promo') ?? undefined);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>(promoCode ? 'annual' : 'monthly');

  // Remove ?promo from the URL so the coupon ID isn't visible or copyable
  useEffect(() => {
    if (promoCode) {
      navigate('/settings/subscription', { replace: true });
    }
  }, []);
  const [loading, setLoading] = useState(false);
  const [checkoutStatus, setCheckoutStatus] = useState<'success' | 'canceled' | null>(null);
  const [downgradeModal, setDowngradeModal] = useState<DowngradeModalState | null>(null);
  const [planChangeSuccess, setPlanChangeSuccess] = useState(false);
  const [postUpgradePlanId, setPostUpgradePlanId] = useState<string | null>(null);
  const [unarchiveModal, setUnarchiveModal] = useState<UnarchiveModalState | null>(null);
  const postUpgradeHandled = useRef(false);
  const [postCheckoutTrigger, setPostCheckoutTrigger] = useState(false);
  const postCheckoutHandled = useRef(false);
  // Callback to fire after unarchive modal is dismissed (skip or confirm)
  const afterUnarchiveCallback = useRef<() => void>(() => setPlanChangeSuccess(true));

  // User has an active paid subscription (not trial) with a real Stripe subscription
  const hasActivePaidSub = !isTrial && isActive && !!subscription?.stripe_subscription_id;

  // After upgrade + refreshProfile, decide whether to show unarchive modal or go straight to success
  useEffect(() => {
    if (!postUpgradePlanId || !userProfile || postUpgradeHandled.current) return;
    postUpgradeHandled.current = true;

    const planId = postUpgradePlanId;
    setPostUpgradePlanId(null);

    const newLimit = PLAN_COURSE_LIMITS[planId] ?? 999;
    const archivedSubjects = (userProfile.subjects ?? []).filter((s: any) => s.is_archived);
    const activeCount = (userProfile.subjects ?? []).filter((s: any) => !s.is_archived).length;
    const availableSlots = newLimit >= 999 ? archivedSubjects.length : Math.max(0, newLimit - activeCount);

    afterUnarchiveCallback.current = () => setPlanChangeSuccess(true);
    if (archivedSubjects.length > 0 && availableSlots > 0) {
      setUnarchiveModal({ planId, availableSlots, archivedSubjects, selectedIds: [] });
    } else {
      setPlanChangeSuccess(true);
    }
  }, [postUpgradePlanId, userProfile]);

  useEffect(() => {
    const status = searchParams.get('checkout');
    if (status === 'success' || status === 'canceled') {
      navigate('/settings/subscription', { replace: true });
      if (status === 'success') {
        // Refresh profile first so archived subjects reflect webhook changes
        refreshProfile().then(() => {
          postCheckoutHandled.current = false;
          setPostCheckoutTrigger(true);
        });
      } else {
        setCheckoutStatus('canceled');
      }
    }
  }, []);

  // After checkout success + refreshProfile, decide whether to show unarchive modal
  useEffect(() => {
    if (!postCheckoutTrigger || !userProfile || postCheckoutHandled.current) return;
    postCheckoutHandled.current = true;
    setPostCheckoutTrigger(false);

    const subTier = (userProfile as any).subscription?.tier ?? 'specialist';
    const newLimit = PLAN_COURSE_LIMITS[subTier] ?? 999;
    const archivedSubjects = (userProfile.subjects ?? []).filter((s: any) => s.is_archived);
    const activeCount = (userProfile.subjects ?? []).filter((s: any) => !s.is_archived).length;
    const availableSlots = newLimit >= 999 ? archivedSubjects.length : Math.max(0, newLimit - activeCount);

    afterUnarchiveCallback.current = () => setCheckoutStatus('success');
    if (archivedSubjects.length > 0 && availableSlots > 0) {
      setUnarchiveModal({ planId: subTier, availableSlots, archivedSubjects, selectedIds: [] });
    } else {
      setCheckoutStatus('success');
    }
  }, [postCheckoutTrigger, userProfile]);

  const getPlanAction = (planId: string): PlanAction => {
    // Current plan (only for active paid, not trial)
    if (!isTrial && isActive && tier === planId) return 'current';
    // Has an active paid subscription → upgrade or downgrade
    if (!isTrial && isActive) {
      const currentOrder = TIER_ORDER[tier] ?? 0;
      const targetOrder = TIER_ORDER[planId] ?? 0;
      return targetOrder > currentOrder ? 'upgrade' : 'downgrade';
    }
    // Trial or no subscription → subscribe
    return 'subscribe';
  };

  const handlePlanAction = async (planId: string, subjectsToKeep?: string[]) => {
    setLoading(true);
    try {
      if (hasActivePaidSub) {
        // Existing paid subscriber: modify the subscription in place (no new Stripe sub)
        await subscriptionAPI.changePlan(planId, billingCycle, subjectsToKeep);
        trackPlanChanged(planId, billingCycle);
        await refreshProfile();
        postUpgradeHandled.current = false;
        setPostUpgradePlanId(planId);
        setLoading(false);
      } else {
        // New subscriber or trial user: go through Stripe Checkout
        // Apply promo coupon only on annual billing (that's what the email offer covers)
        const couponId = (billingCycle === 'annual' && promoCode) ? promoCode : undefined;
        trackCheckoutStarted(planId, billingCycle, !!couponId);
        const response = await subscriptionAPI.createCheckoutSession(planId, billingCycle, subjectsToKeep, couponId);
        window.location.href = response.url;
      }
    } catch (error) {
      console.error('Failed to change plan:', error);
      alert('Failed to update plan. Please try again.');
      setLoading(false);
    }
  };

  const handlePlanClick = (plan: PricingPlan) => {
    const action = getPlanAction(plan.id);
    if (action === 'downgrade' || action === 'subscribe') {
      const newLimit = PLAN_COURSE_LIMITS[plan.id] ?? 1;
      if (newLimit < 999) {
        // Only count active (non-archived) subjects against the limit
        const activeSubjects = (userProfile?.subjects ?? []).filter((s: any) => !s.is_archived);
        if (activeSubjects.length > newLimit) {
          setDowngradeModal({ targetPlan: plan, selectedIds: [], isSubscribe: action === 'subscribe' });
          return;
        }
      }
    }
    handlePlanAction(plan.id);
  };

  const handleManageBilling = async () => {
    setLoading(true);
    try {
      const response = await subscriptionAPI.createPortalSession();
      window.location.href = response.url;
    } catch (error) {
      console.error('Failed to open billing portal:', error);
      alert('Failed to open billing portal. Please try again.');
      setLoading(false);
    }
  };

  const handleConfirmDowngrade = () => {
    if (!downgradeModal) return;
    const selectedIds = downgradeModal.selectedIds;
    setDowngradeModal(null);
    handlePlanAction(downgradeModal.targetPlan.id, selectedIds);
  };

  const toggleUnarchiveSelection = (subjectId: string) => {
    if (!unarchiveModal) return;
    const { selectedIds, availableSlots } = unarchiveModal;
    if (selectedIds.includes(subjectId)) {
      setUnarchiveModal({ ...unarchiveModal, selectedIds: selectedIds.filter(id => id !== subjectId) });
    } else if (selectedIds.length < availableSlots) {
      setUnarchiveModal({ ...unarchiveModal, selectedIds: [...selectedIds, subjectId] });
    }
  };

  const handleConfirmUnarchive = async () => {
    if (!unarchiveModal) return;
    const { selectedIds } = unarchiveModal;
    const callback = afterUnarchiveCallback.current;
    setUnarchiveModal(null);
    if (selectedIds.length > 0) {
      setLoading(true);
      try {
        await Promise.all(selectedIds.map(id => profileAPI.unarchiveSubject(id)));
        await refreshProfile();
      } catch (error) {
        console.error('Failed to unarchive subjects:', error);
      } finally {
        setLoading(false);
      }
    }
    callback();
  };

  const toggleSubjectSelection = (subjectId: string) => {
    if (!downgradeModal) return;
    const newLimit = PLAN_COURSE_LIMITS[downgradeModal.targetPlan.id] ?? 1;
    const { selectedIds } = downgradeModal;

    if (selectedIds.includes(subjectId)) {
      // Deselect
      setDowngradeModal({ ...downgradeModal, selectedIds: selectedIds.filter(id => id !== subjectId) });
    } else if (selectedIds.length < newLimit) {
      // Select (if under limit)
      setDowngradeModal({ ...downgradeModal, selectedIds: [...selectedIds, subjectId] });
    }
  };

  const getActionButtonLabel = (action: PlanAction): string => {
    switch (action) {
      case 'current': return 'Current plan';
      case 'upgrade': return 'Upgrade';
      case 'downgrade': return 'Downgrade';
      case 'subscribe': return 'Subscribe';
    }
  };

  const getActionButtonClass = (action: PlanAction): string => {
    switch (action) {
      case 'current':
        return 'w-full bg-gray-600 text-gray-400 py-2 px-4 rounded-lg cursor-not-allowed';
      case 'upgrade':
        return 'w-full bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary/90 transition disabled:opacity-50';
      case 'downgrade':
        return 'w-full bg-transparent border border-gray-500 text-gray-300 py-2 px-4 rounded-lg hover:bg-gray-800 hover:text-white transition disabled:opacity-50';
      case 'subscribe':
        return 'w-full bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary/90 transition disabled:opacity-50';
    }
  };

  // Downgrade modal state — only show active (non-archived) subjects to pick from
  const downgradeNewLimit = downgradeModal ? (PLAN_COURSE_LIMITS[downgradeModal.targetPlan.id] ?? 1) : 0;
  const downgradeSubjects = (userProfile?.subjects ?? []).filter((s: any) => !s.is_archived);
  const canConfirmDowngrade = downgradeModal !== null && downgradeModal.selectedIds.length === downgradeNewLimit;

  return (
    <Layout>
      <div className="py-12 px-4">
        <div className="max-w-7xl mx-auto">

          {/* Promo banner — shown when arriving from a discount link */}
          {promoCode && (
            <div className="bg-purple-900/40 border border-purple-500 rounded-lg px-5 py-4 mb-6 flex items-center gap-3">
              <span className="text-xl">🎁</span>
              <div>
                <p className="text-white font-semibold text-sm">Special offer applied: 10% off any annual plan</p>
                <p className="text-purple-300 text-xs mt-0.5">Select an annual plan below and the discount will be applied automatically at checkout.</p>
              </div>
            </div>
          )}

          {/* Current subscription status */}
          <div className="bg-[#111827] rounded-lg border border-gray-600 p-6 mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Current subscription</h2>
            {subscription ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Plan</span>
                  <span className="text-white font-semibold capitalize">{isTrial ? 'Free trial' : tier}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Status</span>
                  <span className={`font-semibold ${isActive ? 'text-green-400' : 'text-red-400'}`}>
                    {isTrial ? 'Trial' : subscription.status}
                  </span>
                </div>
                {daysLeft !== null && (
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">{isTrial ? 'Trial ends in' : 'Renews in'}</span>
                    <span className="text-white">{daysLeft} day{daysLeft === 1 ? '' : 's'}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-gray-300">Active courses</span>
                  <span className="text-white">{userProfile?.subjects?.length || 0} / {subscription.course_limit}</span>
                </div>
                {!isTrial && !isComplimentary && (
                  <button
                    onClick={handleManageBilling}
                    disabled={loading}
                    className="mt-4 w-full bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary/90 transition disabled:opacity-50"
                  >
                    Manage billing
                  </button>
                )}
              </div>
            ) : (
              <p className="text-gray-400">No subscription found.</p>
            )}
          </div>

          {/* Billing cycle toggle */}
          <div className="flex justify-center mb-8">
            <div className="bg-[#111827] rounded-lg p-1 border border-gray-600 flex">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-11 py-6 rounded-md transition ${
                  billingCycle === 'monthly' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <div className="relative">
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`px-11 py-6 rounded-md transition ${
                    billingCycle === 'annual' ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  Annual
                </button>
                <div className="absolute top-0 right-0 w-11 h-11 bg-green-400 rounded-full flex flex-col items-center justify-center rotate-12 shadow-lg pointer-events-none select-none">
                  <span className="text-gray-900 font-extrabold text-xs leading-none">2 mo</span>
                  <span className="text-gray-900 font-extrabold text-xs leading-none">free</span>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing plans */}
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {PLANS.map((plan) => {
              const price = billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
              const isPromoActive = !!promoCode && billingCycle === 'annual';
              const discountedPrice = isPromoActive ? Math.round(price * 0.9 * 100) / 100 : null;
              const action = getPlanAction(plan.id);

              return (
                <div
                  key={plan.id}
                  className={`bg-[#111827] rounded-lg border p-6 flex flex-col ${
                    action === 'current' ? 'border-primary' : 'border-gray-600'
                  }`}
                >
                  <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
                  <div className="mb-4">
                    {isPromoActive ? (
                      <>
                        <span className="text-lg text-gray-500 line-through mr-2">${price}</span>
                        <span className="text-3xl font-bold text-purple-400">${discountedPrice}</span>
                        <span className="text-gray-400">/yr</span>
                        <span className="ml-2 text-xs font-semibold text-purple-300 bg-purple-900/40 border border-purple-700 rounded px-1.5 py-0.5">10% off</span>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-white">${price}</span>
                        <span className="text-gray-400">/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
                      </>
                    )}
                  </div>

                  <ul className="space-y-2 mb-4 flex-1">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="text-gray-300 text-sm flex items-start">
                        <span className="text-green-400 mr-2 flex-shrink-0">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Pushed to bottom */}
                  <div className="mt-auto">
                    <p className="text-xs text-gray-400 mb-4">{plan.goodToKnow}</p>
                    <button
                      onClick={() => { if (action !== 'current') handlePlanClick(plan); }}
                      disabled={action === 'current' || loading}
                      className={getActionButtonClass(action)}
                    >
                      {getActionButtonLabel(action)}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>

      {/* Downgrade Warning Modal */}
      {downgradeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#111827] rounded-lg border border-yellow-600 p-6 max-w-lg w-full">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <Warning size={22} weight="fill" className="text-yellow-400 flex-shrink-0" />
                <h2 className="text-white text-xl font-bold">
                  {downgradeModal.isSubscribe
                    ? `Subscribe to ${downgradeModal.targetPlan.name}?`
                    : `Downgrade to ${downgradeModal.targetPlan.name}?`}
                </h2>
              </div>
              <button
                onClick={() => setDowngradeModal(null)}
                className="text-gray-400 hover:text-white transition-colors ml-4 flex-shrink-0"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-gray-300 text-sm mb-4">
              The <strong className="text-white">{downgradeModal.targetPlan.name}</strong> plan only allows{' '}
              <strong className="text-white">{downgradeNewLimit} active course{downgradeNewLimit === 1 ? '' : 's'}</strong>, but you currently have{' '}
              <strong className="text-white">{downgradeSubjects.length}</strong> active.{' '}
              Please select which {downgradeNewLimit === 1 ? 'course' : `${downgradeNewLimit} courses`} you'd like to keep active
              {downgradeModal.isSubscribe ? ' before checking out.' : '.'}
            </p>

            <div className="bg-yellow-900/20 border border-yellow-700/50 rounded-lg p-3 mb-5 text-xs text-yellow-200 space-y-1">
              <p>• Your other courses will be <strong>archived</strong> — not deleted.</p>
              <p>• You can re-access archived courses by upgrading your plan again.</p>
              <p>• All lesson history and progress reports remain fully accessible throughout.</p>
            </div>

            <div className="space-y-2 mb-6">
              <p className="text-gray-400 text-xs uppercase tracking-wider mb-3">
                Select {downgradeNewLimit} course{downgradeNewLimit === 1 ? '' : 's'} to keep active ({downgradeModal.selectedIds.length}/{downgradeNewLimit} selected)
              </p>
              {downgradeSubjects.map((subject: any) => {
                const isSelected = downgradeModal.selectedIds.includes(subject.id);
                const isDisabled = !isSelected && downgradeModal.selectedIds.length >= downgradeNewLimit;
                return (
                  <button
                    key={subject.id}
                    onClick={() => toggleSubjectSelection(subject.id)}
                    disabled={isDisabled}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/10 text-white'
                        : isDisabled
                        ? 'border-gray-700 bg-transparent text-gray-600 cursor-not-allowed'
                        : 'border-gray-600 bg-transparent text-gray-300 hover:border-gray-400 hover:text-white'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'border-primary bg-primary' : 'border-gray-500'
                    }`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    <span className="font-medium">{subject.name}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDowngradeModal(null)}
                className="px-4 py-2 bg-transparent border border-gray-600 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDowngrade}
                disabled={!canConfirmDowngrade || loading}
                className="px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {downgradeModal.isSubscribe ? 'Continue to Checkout' : 'Confirm Downgrade'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unarchive Subjects Modal — shown after upgrade if archived subjects exist */}
      {unarchiveModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#111827] rounded-lg border border-primary p-6 max-w-lg w-full">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-2">
                <ArrowCounterClockwise size={22} weight="bold" className="text-primary flex-shrink-0" />
                <h2 className="text-white text-xl font-bold">Restore Archived Courses</h2>
              </div>
              <button
                onClick={() => { setUnarchiveModal(null); afterUnarchiveCallback.current(); }}
                className="text-gray-400 hover:text-white transition-colors ml-4 flex-shrink-0"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-gray-300 text-sm mb-4">
              Your plan upgrade has freed up{' '}
              <strong className="text-white">
                {unarchiveModal.availableSlots} slot{unarchiveModal.availableSlots === 1 ? '' : 's'}
              </strong>.
              Select the courses you'd like to restore from your archive.
            </p>

            <div className="space-y-2 mb-6">
              <p className="text-gray-400 text-xs uppercase tracking-wider mb-3">
                Select up to {unarchiveModal.availableSlots} course{unarchiveModal.availableSlots === 1 ? '' : 's'}{' '}
                ({unarchiveModal.selectedIds.length}/{unarchiveModal.availableSlots} selected)
              </p>
              {unarchiveModal.archivedSubjects.map((subject) => {
                const isSelected = unarchiveModal.selectedIds.includes(subject.id);
                const isDisabled = !isSelected && unarchiveModal.selectedIds.length >= unarchiveModal.availableSlots;
                return (
                  <button
                    key={subject.id}
                    onClick={() => toggleUnarchiveSelection(subject.id)}
                    disabled={isDisabled}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/10 text-white'
                        : isDisabled
                        ? 'border-gray-700 bg-transparent text-gray-600 cursor-not-allowed'
                        : 'border-gray-600 bg-transparent text-gray-300 hover:border-gray-400 hover:text-white'
                    }`}
                  >
                    <span className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? 'border-primary bg-primary' : 'border-gray-500'
                    }`}>
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    <span className="font-medium">{subject.name}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setUnarchiveModal(null); afterUnarchiveCallback.current(); }}
                className="px-4 py-2 bg-transparent border border-gray-600 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Skip
              </button>
              <button
                onClick={handleConfirmUnarchive}
                disabled={unarchiveModal.selectedIds.length === 0 || loading}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Restore Selected
              </button>
            </div>
          </div>
        </div>
      )}

      {planChangeSuccess && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#111827] rounded-lg border border-green-500 p-8 max-w-md w-full text-center">
            <CheckCircle size={64} weight="fill" className="text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Plan Updated!</h2>
            <p className="text-gray-300 mb-6">
              Your subscription has been updated successfully.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      )}

      {checkoutStatus && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`bg-[#111827] rounded-lg border p-8 max-w-md w-full text-center ${
            checkoutStatus === 'success' ? 'border-green-500' : 'border-gray-600'
          }`}>
            {checkoutStatus === 'success' ? (
              <>
                <CheckCircle size={64} weight="fill" className="text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">Subscription Activated!</h2>
                <p className="text-gray-300 mb-6">
                  Your subscription is now active. Enjoy all the features of your plan!
                </p>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition"
                >
                  Go to Dashboard
                </button>
              </>
            ) : (
              <>
                <XCircle size={64} className="text-gray-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">Checkout Canceled</h2>
                <p className="text-gray-300 mb-6">
                  No charges were made. You can subscribe whenever you're ready.
                </p>
                <button
                  onClick={() => setCheckoutStatus(null)}
                  className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition"
                >
                  Back to Plans
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
